import type { FieldDefinition, Sort } from "@estelle/domain";
import { ListChecks } from "lucide-react";
import { use, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

import { isNumericField, renderCell } from "@/components/table/cells";
import { ListToolsSlotContext } from "@/components/table/list-tools";
import { ListWithFooter } from "@/components/table/ListFooter";
import { SortableHeader } from "@/components/table/SortableHeader";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

interface DataTableProps<T> {
  /** Columns in display order, already resolved by the column chooser. */
  fields: FieldDefinition[];
  rows: readonly T[];
  getRowId: (row: T) => string;
  /**
   * The row flattened to the field names the module declares.
   *
   * The same projection the adapter filters and sorts on. Passing it rather
   * than reading properties off the row keeps the table from knowing the
   * shape of what it renders, which is what lets one table serve ten modules.
   */
  project: (row: T) => Record<string, unknown>;
  sort: Sort[];
  onToggleSort: (field: FieldDefinition, additive: boolean) => void;
  onRowClick?: (row: T) => void;
  /** Overrides the default renderer for one column. */
  renderOverride?: (field: FieldDefinition, row: T) => ReactNode | undefined;
  emptyState?: ReactNode;
  /**
   * A section-drawn card for one row on a narrow screen, replacing the
   * generic label/value stack below.
   *
   * The generic card is a column dumped into a `dl` — fine for a list that is
   * really just its columns read vertically, wrong for Orders' reference
   * layout (#350), which puts the total beside the order number, folds three
   * fields into one line with the customer, and reads a status pair as pills
   * rather than a label column. Optional: every other section still gets the
   * generic card for free.
   */
  mobileRow?: (row: T, projection: Record<string, unknown>) => ReactNode;
  /**
   * A heading shown above a run of consecutive rows sharing the same key —
   * Shopify's "Today" / "Yesterday" day headers over Orders (#350).
   *
   * Rows are grouped by adjacency, not collected and re-sorted: this draws a
   * heading wherever the key changes, so grouping only reads as intended
   * under whatever sort already keeps same-day rows together (the default,
   * placed-date-descending, does). It is mobile-only, matching Shopify's own
   * list, which does not repeat these headings on desktop.
   */
  mobileGroupBy?: (row: T) => string;
  /**
   * The pager, rendered once here rather than by every screen (#382).
   *
   * Below `md` it is the last thing in the scrolling list — after the last
   * card, not pinned to the viewport — so it costs no space until someone
   * scrolls to the end, the way Shopify's own mobile lists work. From `md`
   * up it stays fixed under the table, because the sticky header depends on
   * the table being the only thing that scrolls.
   *
   * Hidden automatically when there are no rows, so a screen never has to
   * repeat the `rows.length > 0` guard that used to sit beside every
   * `<Pagination>` call.
   */
  footer?: ReactNode;
  /**
   * Extra classes for one row, desktop and mobile alike: a cancelled order
   * struck through, for instance, so it stays in the list but reads as
   * cancelled at a glance.
   */
  rowClassName?: (row: T) => string | undefined;
  /**
   * Totals for the whole filtered set, not just the rows on this page (E1,
   * #441) — Stock Levels' By warehouse layout, where a column of pieces per
   * warehouse wants its sum under it.
   *
   * Desktop draws a row after the last, pinned to the foot of the scrolling
   * table so it stays in view through a long page. A phone has no columns to
   * put figures under, so it gets `card` instead: a block before the first
   * row, where a total is read before the rows it sums. Hidden with the rows
   * when there are none.
   */
  totals?: {
    /** One cell of the desktop row, per column in display order. */
    cell: (field: FieldDefinition, index: number) => ReactNode;
    /** The phone's version of the same figures. */
    card: ReactNode;
  };
  /**
   * Checkboxes, for a list with bulk actions (#634). The selection itself is
   * the screen's (`useSelection`), kept across pages; the table only draws
   * the boxes: a column of them on a desktop, with the header box ticking the
   * page, and on a phone a "Select" button that turns them on, so a scroll
   * never ticks a row.
   */
  selection?: TableSelection<T>;
  /** Something beside a column's heading — Stage's legend (#643). */
  headerExtra?: (field: FieldDefinition) => ReactNode;
}

export interface TableSelection<T> {
  isSelected: (row: T) => boolean;
  onToggle: (row: T, on: boolean) => void;
  /** The header box: tick or untick every selectable row on this page. */
  onTogglePage: (rows: readonly T[], on: boolean) => void;
  /** A row no action applies to gets no box. Absent, every row does. */
  selectable?: (row: T) => boolean;
  /** How the box names a row to a screen reader: "Select #EL1390". */
  labelOf: (row: T) => string;
  /**
   * Something is selected, on this page or another: the phone keeps its boxes
   * on, so turning a page never hides what is being gathered.
   */
  active?: boolean;
}

/**
 * The one table every list view uses.
 *
 * Built once here and inherited: a module that ships its own table is how
 * two screens end up disagreeing about how a date is written or where a
 * total is aligned.
 *
 * The header is sticky and the body scrolls inside this container rather than
 * the page, so column headings stay readable through three hundred orders —
 * which is the only reason a header row exists at all.
 *
 * Below `md` the same rows are stacked as cards (#346). Eight columns on a
 * 390px screen is a table read by dragging it sideways one column at a time,
 * which is how someone checking an order on the warehouse floor misses the
 * column they came for. The cards show every chosen column: which ones matter
 * is the column chooser's answer to give, not this component's.
 */
export function DataTable<T>({
  fields,
  rows,
  getRowId,
  project,
  sort,
  onToggleSort,
  onRowClick,
  renderOverride,
  emptyState,
  mobileRow,
  mobileGroupBy,
  footer,
  rowClassName,
  totals,
  selection,
  headerExtra,
}: DataTableProps<T>) {
  const isMobile = useIsMobile();
  const toolsSlot = use(ListToolsSlotContext);
  // On a phone the boxes are off until "Select" is pressed (#634), and stay on
  // while anything is selected.
  const [selectingState, setSelecting] = useState(false);
  const selecting = selectingState || selection?.active === true;

  if (rows.length === 0 && emptyState) return <>{emptyState}</>;

  const selectableRows = selection
    ? rows.filter((row) => selection.selectable?.(row) ?? true)
    : [];
  // Ticked only when every row on the page is: the box ticks the page.
  const pageState =
    selection !== undefined &&
    selectableRows.length > 0 &&
    selectableRows.every((row) => selection.isSelected(row));

  function rowBox(row: T) {
    if (!selection) return null;
    if (!(selection.selectable?.(row) ?? true)) {
      return <span aria-hidden className="inline-block size-4" />;
    }
    return (
      <Checkbox
        aria-label={selection.labelOf(row)}
        checked={selection.isSelected(row)}
        onClick={(event) => {
          event.stopPropagation();
        }}
        onCheckedChange={(checked) => {
          selection.onToggle(row, checked === true);
        }}
        className="max-md:size-5"
      />
    );
  }

  // Hidden once there are no rows, so a screen never has to repeat the
  // `rows.length > 0` guard that used to sit beside every `<Pagination>`.
  const pagerContent = rows.length > 0 ? footer : undefined;

  function cell(
    field: FieldDefinition,
    row: T,
    projection: Record<string, unknown>,
  ) {
    return (
      renderOverride?.(field, row) ?? renderCell(field, projection[field.name])
    );
  }

  if (isMobile) {
    // The first column leads the card. It is the one the module declared
    // first and the one the chooser leaves first, which in every list shipped
    // so far is the row's own name — the order number, the SKU, the person.
    const [lead, ...rest] = fields;
    // Derived once, up front, rather than a mutable "previous group" tracked
    // while mapping: a variable reassigned during render is state that has
    // escaped `useState`, and the lint that catches that is right to.
    const groupKeys = mobileGroupBy ? rows.map(mobileGroupBy) : undefined;

    const selectButton = selection ? (
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="min-h-9 shrink-0"
        onClick={() => {
          setSelecting(true);
        }}
      >
        <ListChecks />
        Select
      </Button>
    ) : null;

    return (
      <ListWithFooter footer={pagerContent}>
        {/* "Select" sits in the toolbar's tabs row where there is one (#647);
            the row below appears only while selecting. */}
        {selection && !selecting && toolsSlot
          ? createPortal(selectButton, toolsSlot)
          : null}
        {selection && (selecting || !toolsSlot) ? (
          <div className="bg-background sticky top-0 z-10 flex min-h-11 items-center gap-3 border-b px-4">
            {selecting ? (
              <>
                <label className="flex min-h-11 items-center gap-2 text-sm">
                  <Checkbox
                    checked={pageState}
                    onCheckedChange={(checked) => {
                      selection.onTogglePage(selectableRows, checked === true);
                    }}
                    className="size-5"
                  />
                  Select this page
                </label>
                {selection.active ? null : (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="ml-auto min-h-11"
                    onClick={() => {
                      setSelecting(false);
                    }}
                  >
                    Done
                  </Button>
                )}
              </>
            ) : (
              selectButton
            )}
          </div>
        ) : null}
        <ul className="divide-y">
          {totals ? (
            <li className="bg-muted/40 px-4 py-3">{totals.card}</li>
          ) : null}
          {rows.map((row, index) => {
            const projection = project(row);
            const group = groupKeys?.[index];
            const showHeading =
              group !== undefined && group !== groupKeys?.[index - 1];

            return (
              <li key={getRowId(row)}>
                {showHeading ? (
                  <div className="bg-muted/40 text-muted-foreground px-4 py-1.5 text-xs font-medium">
                    {group}
                  </div>
                ) : null}

                <StackedRow
                  className={cn(
                    rowClassName?.(row),
                    selecting && "flex items-start gap-3",
                  )}
                  onClick={
                    // While selecting, a tap ticks the row rather than
                    // opening it (#634).
                    selecting && selection
                      ? (selection.selectable?.(row) ?? true)
                        ? () => {
                            selection.onToggle(row, !selection.isSelected(row));
                          }
                        : undefined
                      : onRowClick
                        ? () => {
                            onRowClick(row);
                          }
                        : undefined
                  }
                >
                  {selecting ? (
                    <span className="pt-0.5">{rowBox(row)}</span>
                  ) : null}
                  <div className={cn(selecting && "min-w-0 flex-1")}>
                    {mobileRow ? (
                      mobileRow(row, projection)
                    ) : (
                      <>
                        {lead ? (
                          <div className="min-w-0 text-sm font-medium">
                            {cell(lead, row, projection)}
                          </div>
                        ) : null}

                        {rest.length > 0 ? (
                          <dl className="mt-1.5 space-y-1 text-sm">
                            {rest.map((field) => (
                              <div key={field.name} className="flex gap-3">
                                <dt className="text-muted-foreground w-28 shrink-0 truncate text-xs leading-5">
                                  {field.label}
                                </dt>
                                <dd
                                  className={cn(
                                    "min-w-0 flex-1 break-words",
                                    isNumericField(field) && "tabular-nums",
                                  )}
                                >
                                  {cell(field, row, projection)}
                                </dd>
                              </div>
                            ))}
                          </dl>
                        ) : null}
                      </>
                    )}
                  </div>
                </StackedRow>
              </li>
            );
          })}
        </ul>
      </ListWithFooter>
    );
  }

  return (
    <ListWithFooter footer={pagerContent}>
      <table className="w-full border-collapse text-sm">
        <thead className="bg-background sticky top-0 z-10">
          <tr className="border-b">
            {selection ? (
              <th className="w-px py-2 pr-1 pl-3 text-left">
                <Checkbox
                  aria-label="Select every row on this page"
                  checked={pageState}
                  disabled={selectableRows.length === 0}
                  onCheckedChange={(checked) => {
                    selection.onTogglePage(selectableRows, checked === true);
                  }}
                />
              </th>
            ) : null}
            {fields.map((field) => (
              <SortableHeader
                key={field.name}
                field={field}
                sort={sort}
                onToggle={onToggleSort}
                extra={headerExtra?.(field)}
                className={cn(
                  // A property name, not a heading: left over its column and
                  // in the secondary ink, so the rows are what is read.
                  "text-muted-foreground px-3 py-2 text-left font-normal whitespace-nowrap",
                  isNumericField(field) && "text-right",
                )}
              />
            ))}
          </tr>
        </thead>

        <tbody>
          {rows.map((row) => {
            const projection = project(row);

            return (
              <tr
                key={getRowId(row)}
                onClick={
                  onRowClick
                    ? () => {
                        onRowClick(row);
                      }
                    : undefined
                }
                className={cn(
                  "border-b last:border-b-0",
                  onRowClick && "hover:bg-muted cursor-pointer",
                  rowClassName?.(row),
                  selection?.isSelected(row) && "bg-primary/5",
                )}
              >
                {selection ? (
                  <td className="w-px py-1.5 pr-1 pl-3 align-middle">
                    {rowBox(row)}
                  </td>
                ) : null}
                {fields.map((field) => (
                  <td
                    key={field.name}
                    className={cn(
                      "px-3 py-1.5 align-middle",
                      // Dense by default. This is a screen someone reads a
                      // hundred rows of, not a marketing table.
                      // A figure never breaks across lines: "1,299" over
                      // ".00" is two numbers to anyone scanning the column.
                      isNumericField(field) &&
                        "text-right whitespace-nowrap tabular-nums",
                      (field.type === "date" || field.type === "datetime") &&
                        "whitespace-nowrap tabular-nums",
                    )}
                  >
                    {cell(field, row, projection)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>

        {totals ? (
          <tfoot className="bg-muted sticky bottom-0 z-10 shadow-[0_-1px_0_0_var(--color-border)]">
            <tr>
              {selection ? <td /> : null}
              {fields.map((field, index) => (
                <td
                  key={field.name}
                  className={cn(
                    "px-3 py-2 align-middle font-medium",
                    isNumericField(field) && "text-right tabular-nums",
                  )}
                >
                  {totals.cell(field, index)}
                </td>
              ))}
            </tr>
          </tfoot>
        ) : null}
      </table>
    </ListWithFooter>
  );
}

/**
 * One stacked row: the whole card opens the record, as the whole table row
 * does above `md`.
 *
 * A div with a handler rather than a button, for the same reason the table
 * row is a `tr` with a handler: a row carries its own controls — the
 * catalogue's selection checkbox sits in the first column — and a button
 * cannot contain a button. Neither form is reachable by keyboard, which is one
 * gap in one place rather than two that disagree.
 */
function StackedRow({
  onClick,
  className,
  children,
}: {
  onClick?: () => void;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      onClick={onClick}
      className={cn(
        "px-4 py-3",
        onClick && "hover:bg-muted cursor-pointer",
        className,
      )}
    >
      {children}
    </div>
  );
}
